import React, { useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Checkbox,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EventIcon from '@mui/icons-material/Event';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HelpLink from '../common/HelpLink';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import api from '../../services/api';
import StopLossPlanPanel from './StopLossPlanPanel';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useDispatch } from 'react-redux';
import { settingsService } from '../../services/settings.service';
import { setThemeMode } from '../../store/authSlice';
import { AppDispatch } from '../../store';
import { UserSettings as UserSettingsType, OrderType } from '../../types';
import { API_BASE_URL, TOKEN_KEY } from '../../utils/constants';
import ScheduledTasksPanel from './ScheduledTasksPanel';
import OptionsPollingSettings from './OptionsPollingSettings';
import PasswordChangeCard from './PasswordChangeCard';
import OffsiteBackupCard from './OffsiteBackupCard';
import ApiTokensCard from './ApiTokensCard';
import AiSettingsCard from './AiSettingsCard';
import TwoFactorCard from './TwoFactorCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface BrokerCredential {
  userId: string;
  apiKey: string;
  apiSecret: string;
  isActive: boolean;
}

const UserSettings: React.FC = () => {
  const { C } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // ?tab=sl-plan opens the Stop-Loss Plan directly (Dashboard SL tooltips link here).
  const [tabValue, setTabValue] = useState(() => { try { return new URLSearchParams(window.location.search).get('tab') === 'sl-plan' ? 4 : 0; } catch { return 0; } });
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [testingSpeech, setTestingSpeech] = useState(false);
  // Inline broker test result chips (Phase A)
  type TestStatus = 'idle' | 'testing' | 'success' | 'error';
  const [brokerTest, setBrokerTest] = useState<Record<'delta' | 'zerodha', { status: TestStatus; message?: string }>>({
    delta: { status: 'idle' },
    zerodha: { status: 'idle' },
  });
  // Inline Telegram test result chip
  const [telegramTestResult, setTelegramTestResult] = useState<{ status: TestStatus; message?: string }>({ status: 'idle' });
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState<string | null>(null);
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [appUserId, setAppUserId] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Admin panel state
  const [isAdmin, setIsAdmin] = useState(false);
  // Audit A-001 — each user has their OWN webhook secret (GET /user/webhook); the old
  // platform-wide secret is gone, so the admin no longer hands one out.
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [webhookRotating, setWebhookRotating] = useState(false);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [adminActionLoading, setAdminActionLoading] = useState<number | null>(null); // userId being acted on
  const [expiryDialogUserId, setExpiryDialogUserId] = useState<number | null>(null);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState<number | null>(null);

  // Zerodha OAuth status
  const [zerodhaStatus, setZerodhaStatus] = useState<{ connected: boolean; hasCredentials: boolean; accessTokenExpiresAt?: string; lastConnectedAt?: string } | null>(null);
  const [zerodhaLoginLoading, setZerodhaLoginLoading] = useState(false);

  // Self-promote to admin
  const [promoteAdminOpen, setPromoteAdminOpen] = useState(false);
  const [promoteDbPassword, setPromoteDbPassword] = useState('');
  const [promoteLoading, setPromoteLoading] = useState(false);

  const [settings, setSettings] = useState<UserSettingsType>({
    speechAlertEnabled: false,
    telegramNotificationEnabled: false,
    autoTradeEnabled: false,
    themePreference: 'light',
    defaultOrderQuantity: 1,
    entrySplitCount: 3,
    defaultOrderType: OrderType.MARKET,
    maxOrderAmount: undefined,
    portfolioReservePercent: 35,
    liquidationGuardEnabled: true,
    liquidationWarnMmrPercent: 65,
    liquidationTargetMmrPercent: 40,
    requireOrderConfirmation: true,
    autoRefreshEnabled: true,
    refreshIntervalSeconds: 5,
    favoriteSymbols: [],
    inHouseSignalMode: false,
    rockBandEnabled: true
  });

  const [deltaCredentials, setDeltaCredentials] = useState<BrokerCredential>({
    userId: '',
    apiKey: '',
    apiSecret: '',
    isActive: false
  });

  const [zerodhaCredentials, setZerodhaCredentials] = useState<BrokerCredential>({
    userId: '',
    apiKey: '',
    apiSecret: '',
    isActive: false
  });

  useEffect(() => {
    fetchSettings();
    fetchBrokerCredentials();
    api.get('/user/profile').then((r: any) => {
      if (r.data?.id) setAppUserId(r.data.id);
    }).catch(() => {});
    // Try loading admin config — succeeds only if user is admin (suppress toast for 403)
    const noToast = { headers: { _suppressToast: 'true' } };
    api.get('/admin/config', noToast).then((r: any) => {
      if (r.data?.success) setIsAdmin(true);
    }).catch(() => {});
    api.get('/user/webhook', noToast).then((r: any) => {
      if (r.data?.success && r.data.data?.secret) setWebhookSecret(r.data.data.secret);
    }).catch(() => {});
    api.get('/admin/users', noToast).then((r: any) => {
      if (r.data?.success) setAdminUsers(r.data.data?.users || []);
    }).catch(() => {});
    // Zerodha connection status
    api.get('/broker/zerodha/status', noToast).then((r: any) => {
      if (r.data?.success) setZerodhaStatus(r.data.data);
    }).catch(() => {});
  }, []);

  const refreshAdminUsers = () => {
    api.get('/admin/users').then((r: any) => {
      if (r.data?.success) setAdminUsers(r.data.data?.users || []);
    }).catch(() => {});
  };

  const handleAdminAction = async (userId: number, action: 'approve' | 'suspend', endpoint: string) => {
    setAdminActionLoading(userId);
    try {
      await api.put(endpoint);
      refreshAdminUsers();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || `Failed to ${action} user`);
    }
    setAdminActionLoading(null);
  };

  const handleSetExpiry = async () => {
    if (!expiryDialogUserId) return;
    setAdminActionLoading(expiryDialogUserId);
    try {
      await api.put(`/admin/users/${expiryDialogUserId}/expiry`, { expiresAt: expiryDate || null });
      refreshAdminUsers();
      setExpiryDialogUserId(null);
      setExpiryDate('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to set expiry');
    }
    setAdminActionLoading(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUserId) return;
    setAdminActionLoading(deleteConfirmUserId);
    try {
      await api.delete(`/admin/users/${deleteConfirmUserId}`);
      refreshAdminUsers();
      setDeleteConfirmUserId(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete user');
    }
    setAdminActionLoading(null);
  };

  const fetchSettings = async () => {
    try {
      console.log('Fetching settings...');
      setLoading(true);
      setError(null);

      const response = await settingsService.getUserSettings();
      console.log('Fetch response:', response);
      console.log('Fetch response.data:', response.data);

      if (response.data.success && response.data.data) {
        setSettings(response.data.data);

        // Apply theme immediately
        applyTheme(response.data.data.themePreference);

        // Sync speech setting to localStorage so useWebSocket picks it up even without a save
        localStorage.setItem('speechAlertEnabled', String(response.data.data.speechAlertEnabled ?? false));
      }
    } catch (err: any) {
      console.error('Fetch settings error:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokerCredentials = async () => {
    try {
      console.log('Fetching broker credentials...');
      const response = await settingsService.getBrokerCredentials();
      console.log('Broker credentials response:', response.data);

      if (response.data.success && response.data.data) {
        const credentials = response.data.data;

        // Set Delta credentials if available
        if (credentials.delta) {
          setDeltaCredentials({
            userId: credentials.delta.userId || '',
            apiKey: credentials.delta.apiKey || '',
            apiSecret: credentials.delta.apiSecret || '',
            isActive: credentials.delta.isActive || false
          });
        }

        // Set Zerodha credentials if available
        if (credentials.zerodha) {
          setZerodhaCredentials({
            userId: credentials.zerodha.userId || '',
            apiKey: credentials.zerodha.apiKey || '',
            apiSecret: credentials.zerodha.apiSecret || '',
            isActive: credentials.zerodha.isActive || false
          });
        }
      }
    } catch (err: any) {
      console.error('Fetch broker credentials error:', err);
      // Don't show error for broker credentials fetch failure
    }
  };

  const applyTheme = (theme: 'light' | 'dark') => {
    document.documentElement.setAttribute('data-theme', theme);
    // Also update body background for better experience
    if (theme === 'dark') {
      document.body.style.backgroundColor = '#121212';
      document.body.style.color = '#ffffff';
    } else {
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.color = '#000000';
    }
  };

  const handleSaveSettings = async () => {
    try {
      console.log('Save button clicked!');
      console.log('Settings to save:', settings);

      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await settingsService.updateUserSettings(settings);
      console.log('Update response:', response);

      // Apply theme immediately after saving
      applyTheme(settings.themePreference);

      // Sync speech alert preference to localStorage for WebSocket hook
      localStorage.setItem('speechAlertEnabled', String(settings.speechAlertEnabled));

      setSuccess('Settings saved successfully');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.error?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBrokerCredentials = async (broker: 'delta' | 'zerodha') => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const credentials = broker === 'delta' ? deltaCredentials : zerodhaCredentials;

      if (!credentials.userId || !credentials.apiKey || !credentials.apiSecret) {
        setError(`Please fill all ${broker === 'delta' ? 'Delta Exchange' : 'Zerodha'} credential fields`);
        setSaving(false);
        return;
      }

      await settingsService.updateBrokerCredentials({
        brokerName: broker === 'delta' ? 'delta' : 'zerodha',
        ...credentials
      });

      setSuccess(`${broker === 'delta' ? 'Delta Exchange' : 'Zerodha'} credentials saved successfully`);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save broker credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (broker: 'delta' | 'zerodha') => {
    const credentials = broker === 'delta' ? deltaCredentials : zerodhaCredentials;

    if (!credentials.apiKey || !credentials.apiSecret) {
      setBrokerTest(prev => ({ ...prev, [broker]: { status: 'error', message: 'Fill API Key and API Secret first.' } }));
      return;
    }

    setBrokerTest(prev => ({ ...prev, [broker]: { status: 'testing' } }));
    try {
      const r = await api.post<any>('/broker/test-connection', {
        brokerType: broker === 'delta' ? 'DELTA_EXCHANGE' : 'ZERODHA',
        apiKey: credentials.apiKey,
        apiSecret: credentials.apiSecret,
      }, { headers: { _suppressToast: 'true' } });
      const data = r.data || {};
      if (data.success) {
        const acct = data.accountInfo
          ? ` · balance ${data.accountInfo.balance ?? '—'}`
          : '';
        setBrokerTest(prev => ({ ...prev, [broker]: { status: 'success', message: `Connected${acct}` } }));
      } else {
        setBrokerTest(prev => ({ ...prev, [broker]: { status: 'error', message: data.error?.message || data.message || 'Test failed' } }));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error?.details || err?.response?.data?.error?.message || err?.message || 'Test failed';
      setBrokerTest(prev => ({ ...prev, [broker]: { status: 'error', message: msg } }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      // Fetch backup as blob and trigger download
      const { default: axios } = await import('axios');
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await axios.get(`${API_BASE_URL}/system/backup`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `trading_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess('Backup downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Failed to download backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setRestoreLoading(true);
      setRestoreResult(null);
      const text = await file.text();
      const backup = JSON.parse(text);
      const { default: axios } = await import('axios');
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await axios.post(`${API_BASE_URL}/system/restore`, backup, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (response.data.success) {
        setRestoreResult(response.data.message || 'Restore completed successfully');
        setSuccess('Restore completed successfully');
      } else {
        setError('Restore failed: ' + (response.data.error?.message || 'Unknown error'));
      }
    } catch (err: any) {
      setError('Failed to restore backup: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setRestoreLoading(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  const handleCleanupData = async () => {
    if (!window.confirm('This will permanently delete ALL Trades, Orders, Alerts and Logs. Instruments and Settings will NOT be deleted.\n\nAre you sure? This cannot be undone.')) return;
    try {
      setCleanupLoading(true);
      const { default: axios } = await import('axios');
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await axios.post(`${API_BASE_URL}/system/cleanup`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        setSuccess(response.data.message || 'Data cleaned up successfully');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError('Cleanup failed: ' + (response.data.error?.message || 'Unknown error'));
      }
    } catch (err: any) {
      setError('Failed to clean up data: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleMigratePrefs = async () => {
    if (!window.confirm('This will copy your existing Watchlist/Price Level/AutoTrade settings from the global instruments table into the new per-user preferences table.\n\nSafe to run multiple times. Run this once after upgrading.')) return;
    try {
      setMigrationLoading(true);
      const resp = await api.post<any>('/admin/migrate-instrument-prefs', {});
      if (resp.data.success) {
        setSuccess(`Migration complete: ${resp.data.data?.migrated} instruments migrated`);
        setTimeout(() => setSuccess(null), 6000);
      } else {
        setError('Migration failed');
      }
    } catch (err: any) {
      setError('Migration failed: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleTestSpeech = () => {
    setTestingSpeech(true);
    try {
      const msg = new SpeechSynthesisUtterance('Buy. Nifty.');
      msg.onend = () => setTestingSpeech(false);
      msg.onerror = () => { setTestingSpeech(false); setError('Speech synthesis failed. Ensure your browser allows audio and Web Speech API is supported.'); };
      window.speechSynthesis.speak(msg);
    } catch {
      setTestingSpeech(false);
      setError('Speech synthesis is not supported in this browser.');
    }
  };

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    setTelegramTestResult({ status: 'testing' });
    try {
      const res = await settingsService.testTelegram();
      if (res.data.success) {
        setTelegramTestResult({ status: 'success', message: 'Test message delivered' });
        setSuccess('Test message sent to Telegram successfully!');
      } else {
        const msg = res.data.error?.message || 'Unknown error';
        setTelegramTestResult({ status: 'error', message: msg });
        setError('Telegram test failed: ' + msg);
      }
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || 'Check your Bot Token and Chat ID';
      setTelegramTestResult({ status: 'error', message: msg });
      setError('Failed to send test Telegram message. ' + msg);
    } finally {
      setTestingTelegram(false);
    }
  };

  // Render helper for inline test-result chips
  const renderTestChip = (r: { status: 'idle' | 'testing' | 'success' | 'error'; message?: string }) => {
    if (r.status === 'idle') return null;
    if (r.status === 'testing') return <Chip label="Testing…" size="small" sx={{ ml: 1, bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 600 }} />;
    if (r.status === 'success') return (
      <Tooltip arrow title={r.message || 'OK'}>
        <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={r.message || 'Connected'} size="small" sx={{ ml: 1, bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, maxWidth: 280, '& .MuiChip-icon': { color: C.bull } }} />
      </Tooltip>
    );
    return (
      <Tooltip arrow title={r.message || 'Failed'}>
        <Chip icon={<BlockIcon sx={{ fontSize: 14 }} />} label={r.message || 'Failed'} size="small" sx={{ ml: 1, bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, maxWidth: 280, '& .MuiChip-icon': { color: C.bear } }} />
      </Tooltip>
    );
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setSettings({ ...settings, themePreference: newTheme });
    applyTheme(newTheme);
  };

  const handleZerodhaLogin = async () => {
    try {
      setZerodhaLoginLoading(true);
      const r = await api.get<any>('/broker/zerodha/initiate-login');
      if (r.data?.data?.loginUrl) {
        window.open(r.data.data.loginUrl, '_blank');
        setSuccess('Zerodha login page opened. After authorizing, come back here — the app will handle the callback automatically.');
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError('Could not get Zerodha login URL. Please save your API key first.');
      }
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Failed to initiate Zerodha login');
    } finally {
      setZerodhaLoginLoading(false);
    }
  };

  const handlePromoteAdmin = async () => {
    try {
      setPromoteLoading(true);
      const r = await api.post<any>('/auth/promote-admin', { dbPassword: promoteDbPassword });
      if (r.data?.success) {
        setPromoteAdminOpen(false);
        setPromoteDbPassword('');
        setIsAdmin(true);
        setSuccess('Admin access granted! Please refresh the page to see the Admin tab.');
        setTimeout(() => setSuccess(null), 6000);
      }
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Failed to claim admin access. Check your DB password.');
    } finally {
      setPromoteLoading(false);
    }
  };

  const handleGrantRevokeAdmin = async (userId: number, grantAdmin: boolean) => {
    setAdminActionLoading(userId);
    try {
      await api.put(`/admin/users/${userId}/role`, { role: grantAdmin ? 'admin' : 'user' });
      refreshAdminUsers();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update role');
    }
    setAdminActionLoading(null);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Trading Preferences" />
          <Tab label="Notifications" />
          <Tab label="Broker Credentials" />
          <Tab label="System" />
          {/* 2026-11-03 — ISL / TSL / Target tabs merged into one Stop-Loss Plan. */}
          <Tab label="Stop-Loss Plan" data-testid="settings-tab-sl-plan" />
          <Tab label="Signal Source" />
          <Tab label="Tasks" />
          <Tab label="Options Polling" />
          <Tab label="AI" sx={{ color: C.accent, fontWeight: 700 }} />
          {isAdmin && <Tab label="Admin" sx={{ color: C.warn, fontWeight: 700 }} />}
        </Tabs>
      </Box>

      {/* Trading Preferences Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Order Defaults" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Default Order Type</InputLabel>
                      <Select
                        value={settings.defaultOrderType}
                        label="Default Order Type"
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            defaultOrderType: e.target.value as OrderType
                          })
                        }
                      >
                        <MenuItem value={OrderType.MARKET}>Market</MenuItem>
                        <MenuItem value={OrderType.LIMIT}>Limit</MenuItem>
                        <MenuItem value={OrderType.STOP}>Stop</MenuItem>
                        <MenuItem value={OrderType.STOP_LIMIT}>Stop Limit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Default Order Quantity"
                      type="number"
                      value={settings.defaultOrderQuantity}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          defaultOrderQuantity: Number(e.target.value)
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Entry Splitting (N)</InputLabel>
                      <Select
                        value={settings.entrySplitCount ?? 3}
                        label="Entry Splitting (N)"
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            entrySplitCount: Math.max(1, Math.min(5, Number(e.target.value)))
                          })
                        }
                      >
                        <MenuItem value={1}>1 — single entry (classic)</MenuItem>
                        <MenuItem value={2}>2 — split ½, ½</MenuItem>
                        <MenuItem value={3}>3 — split ¼, ¼, ½</MenuItem>
                        <MenuItem value={4}>4 — split ⅛, ⅛, ¼, ½</MenuItem>
                        <MenuItem value={5}>5 — split 1/16, 1/16, ⅛, ¼, ½</MenuItem>
                      </Select>
                      <FormHelperText>
                        Chart BUY/SHORT splits into this many averaging-down entry levels sharing one SL/Target. The last level always takes half the quantity so each fill recenters the average.
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Max Order Amount (optional)"
                      type="number"
                      value={settings.maxOrderAmount || ''}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          maxOrderAmount: Number(e.target.value) || undefined
                        })
                      }
                      helperText="Leave empty for no limit"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Portfolio Reserve (%)"
                      type="number"
                      value={settings.portfolioReservePercent ?? 35}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          portfolioReservePercent: Math.min(90, Math.max(10, Number(e.target.value)))
                        })
                      }
                      inputProps={{ min: 10, max: 90, step: 1 }}
                      helperText="FREE margin floor. New trades are REJECTED when free margin falls below this. Different from the liquidation guard below."
                    />
                  </Grid>
                  {/* Liquidation Guard. Added after 2026-09-08, when six Delta
                      positions were liquidated in one second and the app — which
                      had free margin at zero on every 15-minute snapshot for the
                      preceding 3h44m — never warned. */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mt: 1, mb: -1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Liquidation Guard</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Warns you before the exchange liquidates your positions, and tells you how much to add.
                        Watches the <b>maintenance margin ratio</b> (MMR = maintenance margin ÷ collateral);
                        Delta liquidates at 100%. This only notifies &mdash; it never places or closes an order.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(settings as any).liquidationGuardEnabled !== false}
                          onChange={(e) =>
                            setSettings({ ...settings, liquidationGuardEnabled: e.target.checked } as any)
                          }
                        />
                      }
                      label="Liquidation warnings"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }} />
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Warn at MMR (%)"
                      type="number"
                      disabled={(settings as any).liquidationGuardEnabled === false}
                      value={(settings as any).liquidationWarnMmrPercent ?? 65}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          liquidationWarnMmrPercent: Math.min(95, Math.max(20, Number(e.target.value)))
                        } as any)
                      }
                      inputProps={{ min: 20, max: 95, step: 1 }}
                      helperText={`Warning at this MMR. Watch ${Math.max(5, ((settings as any).liquidationWarnMmrPercent ?? 65) - 15)}%, Critical ${Math.min(95, ((settings as any).liquidationWarnMmrPercent ?? 65) + 15)}%, Emergency ${Math.min(92, ((settings as any).liquidationWarnMmrPercent ?? 65) + 25)}%. Liquidation is 100%.`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Recover to MMR (%)"
                      type="number"
                      disabled={(settings as any).liquidationGuardEnabled === false}
                      value={(settings as any).liquidationTargetMmrPercent ?? 40}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          liquidationTargetMmrPercent: Math.min(90, Math.max(5, Number(e.target.value)))
                        } as any)
                      }
                      inputProps={{ min: 5, max: 90, step: 1 }}
                      helperText="The alert's &quot;add ₹X&quot; figure is whatever brings MMR back down to this level. Must be below the warning level."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Unfilled Order Timeout (minutes)"
                      type="number"
                      value={(settings as any).unfilledOrderTimeoutMinutes ?? 5}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          unfilledOrderTimeoutMinutes: Math.max(1, Number(e.target.value))
                        } as any)
                      }
                      inputProps={{ min: 1, max: 60, step: 1 }}
                      helperText="Send Telegram alert if a NSE/Zerodha limit order is not filled within this many minutes (default: 5)."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Profit Lock Multiplier (× SL Risk)"
                      type="number"
                      value={(settings as any).additionalSettings?.profitLockMultiplier ?? 5}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          additionalSettings: {
                            ...((settings as any).additionalSettings || {}),
                            profitLockMultiplier: Math.max(1, Number(e.target.value))
                          }
                        } as any)
                      }
                      inputProps={{ min: 1, max: 100, step: 0.5 }}
                      helperText="Show 🔒 when P&L reaches this multiple of your SL risk (e.g. 5 = lock at 5× risk). Applies in Trades and Dashboard."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!(settings as any).additionalSettings?.manualScanReminder?.enabled}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                additionalSettings: {
                                  ...((settings as any).additionalSettings || {}),
                                  manualScanReminder: {
                                    ...(((settings as any).additionalSettings || {}).manualScanReminder || { every: 4, unit: 'hours' }),
                                    enabled: e.target.checked,
                                  },
                                },
                              } as any)
                            }
                          />
                        }
                        label="Manual Scan & Review Reminder"
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Show an in-app banner reminding you to run a manual scan / review on a fixed cadence. Slots are aligned
                        to clock boundaries (like candle starts) — e.g. every 4 hours fires at 00:00, 04:00, 08:00 … IST. Closing
                        the banner schedules the next slot.
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        <TextField
                          label="Remind every"
                          type="number"
                          size="small"
                          sx={{ width: 140 }}
                          disabled={!(settings as any).additionalSettings?.manualScanReminder?.enabled}
                          value={(settings as any).additionalSettings?.manualScanReminder?.every ?? 4}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              additionalSettings: {
                                ...((settings as any).additionalSettings || {}),
                                manualScanReminder: {
                                  ...(((settings as any).additionalSettings || {}).manualScanReminder || { enabled: true, unit: 'hours' }),
                                  every: Math.max(1, Math.floor(Number(e.target.value) || 1)),
                                },
                              },
                            } as any)
                          }
                          inputProps={{ min: 1, max: 1440, step: 1 }}
                        />
                        <FormControl size="small" sx={{ width: 160 }} disabled={!(settings as any).additionalSettings?.manualScanReminder?.enabled}>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            label="Unit"
                            value={(settings as any).additionalSettings?.manualScanReminder?.unit ?? 'hours'}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                additionalSettings: {
                                  ...((settings as any).additionalSettings || {}),
                                  manualScanReminder: {
                                    ...(((settings as any).additionalSettings || {}).manualScanReminder || { enabled: true, every: 4 }),
                                    unit: e.target.value,
                                  },
                                },
                              } as any)
                            }
                          >
                            <MenuItem value="minutes">Minutes</MenuItem>
                            <MenuItem value="hours">Hours</MenuItem>
                            <MenuItem value="days">Days</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.requireOrderConfirmation}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              requireOrderConfirmation: e.target.checked
                            })
                          }
                        />
                      }
                      label="Require Order Confirmation"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Show confirmation dialog before placing orders
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Phase B — Daily-Loss Circuit Breaker */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader
                title="Risk · Daily-Loss Circuit Breaker"
                subheader="Hard-disable order placement for the rest of the day once today's loss meets the cap."
              />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Daily-loss cap (₹)"
                      type="number"
                      value={settings.dailyLossCap ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setSettings({ ...settings, dailyLossCap: raw === '' ? null : Number(raw) });
                      }}
                      inputProps={{ min: 0, step: 100 }}
                      helperText="₹ amount. Leave blank to clear. Wins do NOT reduce the consumed amount — only realised losses do."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!settings.dailyLossCapEnabled}
                          onChange={(e) => setSettings({ ...settings, dailyLossCapEnabled: e.target.checked })}
                          disabled={settings.dailyLossCap == null || (settings.dailyLossCap ?? 0) <= 0}
                          color="error"
                        />
                      }
                      label={settings.dailyLossCapEnabled ? 'Enforcement ON — orders blocked when breached' : 'Enforcement OFF — Risk Cockpit shows usage only'}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      When ON: order placement is rejected at source for the rest of the day once today's realised loss reaches the cap.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader
                title="TradeDesk · Strict Strategy Exits"
                subheader="How exit SIGNALS from one strategy treat positions opened by another strategy."
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!(settings as any).strictStrategyExits}
                      onChange={(e) => setSettings({ ...settings, strictStrategyExits: e.target.checked } as any)}
                      color="warning"
                    />
                  }
                  label={(settings as any).strictStrategyExits
                    ? 'STRICT — a strategy\'s exit signal only closes positions that same strategy opened'
                    : 'OPEN (default) — any strategy\'s exit signal can flatten the symbol'}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Applies only to strategy exit signals (SELL/COVER). Stop-loss, Target and Trailing exits are
                  ALWAYS active on every position regardless of this setting. Positions without a strategy
                  attribution (manual, webhook) are closeable by any exit signal in both modes.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader
                title="TradeDesk · Auto-Reverse (Stop-and-Reverse)"
                subheader="What an auto ENTRY signal does when an OPPOSITE position is already open."
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={(settings as any).autoReverseEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, autoReverseEnabled: e.target.checked } as any)}
                      color="primary"
                    />
                  }
                  label={(settings as any).autoReverseEnabled !== false
                    ? 'ON (default) — an opposite-direction signal CLOSES the open position, then opens the new side (flip)'
                    : 'OFF — an opposite-direction signal is skipped while a position is open (POSITION_OPEN)'}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Needed for stop-and-reverse strategies (Shaved Doji, and RockBand/Sniper strong reversals), which emit a
                  single opposite-direction signal to flip. The CLOSE leg always runs (closing is always allowed); the OPEN
                  leg still respects the per-instrument <strong>Auto Long / Auto Short</strong> gate — if that direction is
                  OFF, the opposite is closed but the new side is not opened (you end up flat). Same-direction signals never
                  stack. If the opposite close can't be confirmed, the new entry is held (no unintended hedge).
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Display Preferences" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Theme</InputLabel>
                      <Select
                        value={settings.themePreference}
                        label="Theme"
                        onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark')}
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Auto Refresh Interval (seconds)"
                      type="number"
                      value={settings.refreshIntervalSeconds}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          refreshIntervalSeconds: Number(e.target.value)
                        })
                      }
                      disabled={!settings.autoRefreshEnabled}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.autoRefreshEnabled}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              autoRefreshEnabled: e.target.checked
                            })
                          }
                        />
                      }
                      label="Enable Auto Refresh"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Notifications Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Alert Notifications" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.speechAlertEnabled}
                            onChange={(e) =>
                              setSettings({ ...settings, speechAlertEnabled: e.target.checked })
                            }
                          />
                        }
                        label="Speech Alerts"
                      />
                      <Tooltip title="Uses your browser's Web Speech API for text-to-speech alerts. Requires a modern browser (Chrome, Edge, Firefox). Make sure to allow audio permissions. Alerts will announce signal type, symbol, strength, and confirmation status." arrow placement="right">
                        <IconButton size="small" sx={{ ml: 1 }}>
                          <InfoOutlined fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Enable text-to-speech for important alerts
                    </Typography>
                    {settings.speechAlertEnabled && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={handleTestSpeech}
                        disabled={testingSpeech}
                      >
                        {testingSpeech ? 'Speaking...' : 'Test Speech'}
                      </Button>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.telegramNotificationEnabled}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                telegramNotificationEnabled: e.target.checked
                              })
                            }
                          />
                        }
                        label="Telegram Notifications"
                      />
                      <Tooltip title="To set up Telegram notifications: 1) Open Telegram and search for @BotFather. 2) Send /newbot and follow the prompts to create a bot. 3) Copy the bot token provided. 4) Start a chat with your new bot. 5) To find your Chat ID, send a message to @userinfobot or use the Telegram API: https://api.telegram.org/bot<TOKEN>/getUpdates" arrow placement="right">
                        <IconButton size="small" sx={{ ml: 1 }}>
                          <InfoOutlined fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Send alerts and updates via Telegram
                    </Typography>
                  </Grid>
                  {settings.telegramNotificationEnabled && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Telegram Bot Token"
                          value={settings.telegramBotToken || ''}
                          onChange={(e) =>
                            setSettings({ ...settings, telegramBotToken: e.target.value })
                          }
                          size="small"
                          helperText="Get from @BotFather on Telegram"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Telegram ChannelId / Chat ID"
                          value={settings.telegramChatId || ''}
                          onChange={(e) =>
                            setSettings({ ...settings, telegramChatId: e.target.value })
                          }
                          size="small"
                          helperText="Your Telegram Channel ID or Chat ID"
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={handleTestTelegram}
                            disabled={testingTelegram || !settings.telegramBotToken || !settings.telegramChatId}
                          >
                            {testingTelegram ? 'Sending...' : 'Send Test Message'}
                          </Button>
                          {renderTestChip(telegramTestResult)}
                          <Typography variant="caption" color="text.secondary">
                            Sends a test message to your Telegram chat to verify the setup
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Periodic Report Interval (minutes)"
                          type="number"
                          value={settings.telegramReportIntervalMinutes || 30}
                          onChange={e => setSettings({ ...settings, telegramReportIntervalMinutes: parseInt(e.target.value) || 30 } as any)}
                          inputProps={{ min: 5, max: 1440 }}
                          size="small"
                          sx={{ width: 280 }}
                          helperText="Interval for Telegram periodic trade/alert reports"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Broker Credentials Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom align="center">
          Broker Credentials Management
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom align="center" sx={{ mb: 1, fontStyle: 'italic' }}>
          Configure your broker API credentials for symbol importing and trading
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <HelpLink to="broker-setup" label="📖 App Guide → Broker Setup (Delta + Zerodha walkthrough)" />
        </Box>

        <Grid container spacing={3}>
          {/* Phase E — Account password change */}
          <Grid size={{ xs: 12 }}>
            <PasswordChangeCard />
          </Grid>

          {/* H11 / F7 — Two-Factor Authentication (TOTP). The card fetches
              its own state on mount via /user/profile. */}
          <Grid size={{ xs: 12 }}>
            <TwoFactorCard />
          </Grid>

          {/* Phase E.1 — Personal API tokens */}
          <Grid size={{ xs: 12 }}>
            <ApiTokensCard />
          </Grid>

          {/* TradingView Webhook Info */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: C.neutralMuted, border: `1px solid ${C.neutralBorder}` }}>
              <CardHeader title="TradingView Webhook URL" subheader="Use this URL in your TradingView alert Webhook settings" />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This URL carries your User ID and your own secret. It only works for your
                  account, so keep it private. Anyone with it can send alerts as you.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Your User ID"
                    value={appUserId ?? 'Loading...'}
                    InputProps={{ readOnly: true }}
                    helperText="Your numeric app user ID. It is part of the webhook URL."
                  />
                </Box>
                {appUserId && webhookSecret && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Your webhook URL"
                      data-testid="webhook-url"
                      value={`https://earngush.com/webhook/tradingview?userId=${appUserId}&secret=${showWebhookSecret ? webhookSecret : '•'.repeat(12)}`}
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                      helperText="Paste this into TradingView: Alert → Notifications → Webhook URL."
                      sx={{ flex: '1 1 360px' }}
                    />
                    <Button size="small" onClick={() => setShowWebhookSecret(v => !v)}>
                      {showWebhookSecret ? 'Hide' : 'Show'}
                    </Button>
                    <Tooltip title={copySuccess ? 'Copied!' : 'Copy URL'}>
                      <IconButton size="small" onClick={() => {
                        navigator.clipboard.writeText(`https://earngush.com/webhook/tradingview?userId=${appUserId}&secret=${webhookSecret}`);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Issue a new secret. Your current TradingView alerts stop working until you paste the new URL into each of them.">
                      <span>
                        <Button
                          size="small"
                          color="warning"
                          disabled={webhookRotating}
                          onClick={async () => {
                            if (!window.confirm('Issue a new webhook secret? Every TradingView alert that uses the current URL will be rejected until you update it.')) return;
                            setWebhookRotating(true);
                            try {
                              const r: any = await api.post('/user/webhook/rotate');
                              if (r.data?.data?.secret) { setWebhookSecret(r.data.data.secret); setShowWebhookSecret(true); }
                            } catch { /* api interceptor shows the error */ }
                            setWebhookRotating(false);
                          }}
                        >
                          New secret
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {appUserId && !webhookSecret && (
                  <Typography variant="caption" color="text.secondary">Loading your webhook URL…</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Delta Exchange Credentials */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Delta Exchange" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="User ID"
                      value={deltaCredentials.userId}
                      onChange={(e) =>
                        setDeltaCredentials({
                          ...deltaCredentials,
                          userId: e.target.value
                        })
                      }
                      placeholder="Enter your Delta Exchange User ID"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="API Key"
                      value={deltaCredentials.apiKey}
                      onChange={(e) =>
                        setDeltaCredentials({
                          ...deltaCredentials,
                          apiKey: e.target.value
                        })
                      }
                      placeholder="Enter your Delta Exchange API key"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="API Secret"
                      type="password"
                      value={deltaCredentials.apiSecret}
                      onChange={(e) =>
                        setDeltaCredentials({
                          ...deltaCredentials,
                          apiSecret: e.target.value
                        })
                      }
                      placeholder="Enter your Delta Exchange API secret"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deltaCredentials.isActive}
                          onChange={(e) =>
                            setDeltaCredentials({
                              ...deltaCredentials,
                              isActive: e.target.checked
                            })
                          }
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => handleTestConnection('delta')}
                        disabled={brokerTest.delta.status === 'testing'}
                      >
                        Test Connection
                      </Button>
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={() => handleSaveBrokerCredentials('delta')}
                        disabled={saving}
                      >
                        Save
                      </Button>
                      {renderTestChip(brokerTest.delta)}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Zerodha Credentials */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Zerodha" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* One-time setup notice — redirect URI must be configured in Kite developer console */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: C.neutralMuted, border: `1px solid ${C.neutralBorder}` }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: C.neutral, mb: 0.5 }}>
                        ⚙️ One-Time Kite Connect App Setup Required
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        In your Kite Connect developer console (<strong>kite.zerodha.com/connect/apps</strong>), open your app and set the <strong>Redirect URL</strong> to exactly:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'action.hover', borderRadius: 1, px: 1.5, py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, userSelect: 'all' }}>
                          https://earngush.com/zerodha-callback
                        </Typography>
                        <Tooltip title={copySuccess ? 'Copied!' : 'Copy URL'}>
                          <IconButton size="small" onClick={() => { navigator.clipboard.writeText('https://earngush.com/zerodha-callback'); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); }}>
                            <Typography variant="caption">{copySuccess ? '✓' : '📋'}</Typography>
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Without this, the "Login with Zerodha" button will open Kite but the app will never receive the session token.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="User ID"
                      value={zerodhaCredentials.userId}
                      onChange={(e) =>
                        setZerodhaCredentials({
                          ...zerodhaCredentials,
                          userId: e.target.value
                        })
                      }
                      placeholder="Enter your Zerodha User ID"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="API Key"
                      value={zerodhaCredentials.apiKey}
                      onChange={(e) =>
                        setZerodhaCredentials({
                          ...zerodhaCredentials,
                          apiKey: e.target.value
                        })
                      }
                      placeholder="Enter your Zerodha API key"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="API Secret"
                      type="password"
                      value={zerodhaCredentials.apiSecret}
                      onChange={(e) =>
                        setZerodhaCredentials({
                          ...zerodhaCredentials,
                          apiSecret: e.target.value
                        })
                      }
                      placeholder="Enter your Zerodha API secret"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={zerodhaCredentials.isActive}
                          onChange={(e) =>
                            setZerodhaCredentials({
                              ...zerodhaCredentials,
                              isActive: e.target.checked
                            })
                          }
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  {/* Zerodha OAuth status */}
                  {zerodhaStatus && (
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: zerodhaStatus.connected ? C.bullMuted : C.warnMuted, border: `1px solid ${zerodhaStatus.connected ? C.bullBorder : C.warnBorder}` }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: zerodhaStatus.connected ? C.bull : C.warn }}>
                          {zerodhaStatus.connected
                            ? `✅ Zerodha Connected — Token expires: ${zerodhaStatus.accessTokenExpiresAt ? new Date(zerodhaStatus.accessTokenExpiresAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : 'Unknown'}`
                            : zerodhaStatus.hasCredentials
                              ? '⚠️ Credentials saved but not logged in. Click "Login with Zerodha" to authenticate.'
                              : '❌ No Zerodha credentials saved yet.'
                          }
                        </Typography>
                        {zerodhaStatus.lastConnectedAt && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            Last connected: {new Date(zerodhaStatus.lastConnectedAt).toLocaleString('en-IN')}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="medium"
                        onClick={handleZerodhaLogin}
                        disabled={zerodhaLoginLoading}
                      >
                        {zerodhaLoginLoading ? <CircularProgress size={20} color="inherit" /> : '🔐 Login with Zerodha'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => handleTestConnection('zerodha')}
                        disabled={brokerTest.zerodha.status === 'testing'}
                      >
                        Test Connection
                      </Button>
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={() => handleSaveBrokerCredentials('zerodha')}
                        disabled={saving}
                      >
                        Save Credentials
                      </Button>
                      {renderTestChip(brokerTest.zerodha)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Zerodha requires daily re-authentication (access token expires at 6 AM next day).
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Alert severity="warning">
              Your API credentials are encrypted and stored securely. Never share your
              credentials with anyone.
            </Alert>
          </Grid>
        </Grid>
      </TabPanel>

      {/* System Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderLeft: '4px solid #f44336' }}>
              <CardHeader title="Clean Slate" subheader="Delete all Trades, Orders, Alerts and Logs" />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use this to start fresh. Instruments and all Settings (broker credentials, trading preferences) will be preserved. All trade history, alerts, orders and logs will be permanently deleted.
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCleanupData}
                  disabled={cleanupLoading}
                >
                  {cleanupLoading ? 'Cleaning up...' : 'Delete Trades / Orders / Alerts / Logs'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Manual Export" subheader="A JSON file of your own trades, alerts and orders, downloaded to this device" />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Download a JSON copy of your trades, alerts, orders and instruments. Useful for your own records or for moving data between accounts.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleBackup}
                  disabled={backupLoading}
                >
                  {backupLoading ? 'Preparing backup...' : 'Download Backup Now'}
                </Button>
                <Button
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onClick={() => restoreInputRef.current?.click()}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? 'Restoring...' : 'Restore from Backup'}
                </Button>
                <input
                  type="file"
                  accept=".json"
                  ref={restoreInputRef}
                  style={{ display: 'none' }}
                  onChange={handleRestoreFileSelect}
                />
                {restoreResult && (
                  <Typography variant="caption" display="block" color="success.main" sx={{ mt: 1 }}>
                    {restoreResult}
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  This is a manual copy you keep yourself. It is NOT the automatic backup &mdash; the
                  whole database is already backed up off-site every night, and its health is shown
                  in the Off-site Backup card below.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <OffsiteBackupCard />
          </Grid>

          {isAdmin && (
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: '4px solid #0284c7' }}>
                <CardHeader title="Migrate Instrument Preferences" subheader="One-time migration after upgrade" />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Copy your existing Watchlist, Price Level, Auto-Trade, and Investment settings from the global instruments table into the new per-user preferences table. Safe to run multiple times.
                  </Typography>
                  <Button variant="outlined" color="info" onClick={handleMigratePrefs} disabled={migrationLoading}>
                    {migrationLoading ? 'Migrating...' : 'Run Preferences Migration'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
          {!isAdmin && (
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: `4px solid ${C.accent}` }}>
                <CardHeader title="Claim Admin Access" subheader="Only for the app owner — requires the database password" />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    If you are the app owner and know the database password, you can promote yourself to admin without needing Putty/SSH access.
                  </Typography>
                  <Button variant="outlined" color="secondary" onClick={() => setPromoteAdminOpen(true)}>
                    Claim Admin Access
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Stop-Loss Plan (index 4) — replaces the ISL / TSL / Target tabs. */}
      <TabPanel value={tabValue} index={4}>
        <StopLossPlanPanel />
      </TabPanel>

      {/* Signal Source Tab (index 5) */}
      <TabPanel value={tabValue} index={5}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader title="Signal Source" subheader="Choose whether signals come from TradingView webhooks or the in-house strategy engine" />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* TradingView Mode */}
                  <Box
                    onClick={() => setSettings(s => ({ ...s, inHouseSignalMode: false }))}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      border: theme => `2px solid ${!settings.inHouseSignalMode ? theme.palette.primary.main : theme.palette.divider}`,
                      backgroundColor: !settings.inHouseSignalMode ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', border: '2px solid',
                        borderColor: !settings.inHouseSignalMode ? 'primary.main' : 'text.disabled',
                        backgroundColor: !settings.inHouseSignalMode ? 'primary.main' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {!settings.inHouseSignalMode && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff' }} />
                        )}
                      </Box>
                      <Box>
                        <Typography fontWeight={700}>TradingView Alerts (default)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Signals come from TradingView webhook. All webhook alerts are processed normally.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* In-House Mode */}
                  <Box
                    onClick={() => setSettings(s => ({ ...s, inHouseSignalMode: true }))}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      border: theme => `2px solid ${settings.inHouseSignalMode ? theme.palette.success.main : theme.palette.divider}`,
                      backgroundColor: settings.inHouseSignalMode ? 'success.50' : 'background.paper',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', border: '2px solid',
                        borderColor: settings.inHouseSignalMode ? 'success.main' : 'text.disabled',
                        backgroundColor: settings.inHouseSignalMode ? 'success.main' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {settings.inHouseSignalMode && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff' }} />
                        )}
                      </Box>
                      <Box>
                        <Typography fontWeight={700}>In-House Mode</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Signals generated server-side from OHLC data. TradingView webhooks are silently ignored.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Strategy cards — shown only when In-House Mode is selected */}
                  {settings.inHouseSignalMode && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Active Strategies
                      </Typography>
                      <Box sx={{
                        p: 2, border: '1px solid',
                        borderColor: 'divider', borderRadius: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                      }}>
                        <Box>
                          <Typography fontWeight={700}>RockBand</Typography>
                          <Typography variant="body2" color="text.secondary">
                            CTF + HTF2 kernel-smoothed Bollinger Band strategy
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Switch
                            checked={settings.rockBandEnabled ?? true}
                            onChange={e => setSettings(s => ({ ...s, rockBandEnabled: e.target.checked }))}
                            color="success"
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}

                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Scheduled Tasks Tab (index 8) */}
      <TabPanel value={tabValue} index={6}>
        <ScheduledTasksPanel />
      </TabPanel>

      {/* Options Polling Tab (index 9) */}
      <TabPanel value={tabValue} index={7}>
        <OptionsPollingSettings />
      </TabPanel>

      {/* AI Tab (index 10) — appended after Options Polling so no existing panel index shifts.
          The tabs here are positional, so inserting mid-strip would silently re-point ten panels. */}
      <TabPanel value={tabValue} index={8}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <AiSettingsCard />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Admin Tab (index 11 — only visible to admins) */}
      {isAdmin && (
        <TabPanel value={tabValue} index={9}>
          <Grid container spacing={3}>

            {/* Webhook Secret card */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: '4px solid #d97706' }}>
                <CardHeader
                  title="Webhook Configuration"
                  subheader="There is no shared webhook secret. Each user copies their own webhook URL from Settings → Broker Credentials; it works only for their account."
                />
              </Card>
            </Grid>

            {/* User management table */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: `4px solid ${C.accent}` }}>
                <CardHeader
                  title="User Management"
                  subheader="Approve, suspend, set expiry, or delete users. New registrations start as Pending until you approve."
                  action={
                    <Button size="small" variant="outlined" onClick={refreshAdminUsers}>Refresh</Button>
                  }
                />
                <CardContent sx={{ p: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name / Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Webhook URL</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminUsers.map((u: any) => {
                        const isMe = u.id === appUserId;
                        const busy = adminActionLoading === u.id;
                        const statusConfig: Record<string, { label: string; bg: string; color: string }> = {
                          active:    { label: 'Active',    bg: C.bullMuted,    color: C.bull },
                          pending:   { label: 'Pending',   bg: C.warnMuted,    color: C.warn },
                          suspended: { label: 'Suspended', bg: C.bearMuted,    color: C.bear },
                          expired:   { label: 'Expired',   bg: C.dimMuted,     color: C.dim },
                        };
                        const sc = statusConfig[u.status] || statusConfig.active;
                        return (
                          <TableRow key={u.id} sx={{ opacity: busy ? 0.5 : 1, backgroundColor: u.status === 'pending' ? C.warnMuted : 'inherit' }}>
                            <TableCell sx={{ fontWeight: 700, color: isMe ? C.accent : 'inherit' }}>
                              {u.id}{isMe && ' (you)'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{u.name || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.7rem' }} />
                              {u.role === 'admin' && <Chip label="Admin" size="small" sx={{ ml: 0.5, bgcolor: C.accentMuted, color: C.accent, fontWeight: 700, fontSize: '0.65rem' }} />}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ color: u.status === 'expired' ? C.bear : 'inherit' }}>
                                {u.expiresAt ? new Date(u.expiresAt).toLocaleDateString('en-IN') : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-IN') : 'Never'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {u.status === 'active' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {u.webhookUrl}
                                  </Typography>
                                  <Tooltip title="Copy webhook URL">
                                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(u.webhookUrl)}>
                                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {!isMe && (
                                <Stack direction="row" spacing={0.5}>
                                  {(u.status === 'pending' || u.status === 'suspended' || u.status === 'expired') && (
                                    <Tooltip title="Approve / Activate">
                                      <span>
                                        <IconButton size="small" color="success" disabled={busy}
                                          onClick={() => handleAdminAction(u.id, 'approve', `/admin/users/${u.id}/approve`)}>
                                          <CheckCircleIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {u.status === 'active' && (
                                    <Tooltip title="Suspend user immediately">
                                      <span>
                                        <IconButton size="small" color="warning" disabled={busy}
                                          onClick={() => handleAdminAction(u.id, 'suspend', `/admin/users/${u.id}/suspend`)}>
                                          <BlockIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={u.expiresAt ? 'Change / Clear expiry' : 'Set expiry date'}>
                                    <span>
                                      <IconButton size="small" sx={{ color: u.expiresAt ? C.warn : C.dim }} disabled={busy}
                                        onClick={() => { setExpiryDialogUserId(u.id); setExpiryDate(u.expiresAt ? u.expiresAt.split('T')[0] : ''); }}>
                                        <EventIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={u.role === 'admin' ? 'Revoke Admin' : 'Grant Admin'}>
                                    <span>
                                      <IconButton size="small" sx={{ color: u.role === 'admin' ? C.accent : C.dim }} disabled={busy}
                                        onClick={() => handleGrantRevokeAdmin(u.id, u.role !== 'admin')}>
                                        <HourglassTopIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Delete user permanently">
                                    <span>
                                      <IconButton size="small" color="error" disabled={busy}
                                        onClick={() => setDeleteConfirmUserId(u.id)}>
                                        <DeleteForeverIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  {busy && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Set Expiry Dialog */}
          <Dialog open={expiryDialogUserId !== null} onClose={() => setExpiryDialogUserId(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Set Access Expiry</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                User will be automatically blocked from logging in after this date. Leave blank to remove expiry.
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                value={expiryDate}
                onChange={e => setExpiryDate(e.target.value)}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpiryDialogUserId(null)}>Cancel</Button>
              {expiryDate && (
                <Button color="warning" onClick={() => { setExpiryDate(''); }} startIcon={<LockOpenIcon />}>
                  Clear Expiry
                </Button>
              )}
              <Button variant="contained" onClick={handleSetExpiry} startIcon={<EventIcon />}>
                {expiryDate ? 'Set Expiry' : 'Remove Expiry'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirm Dialog */}
          <Dialog open={deleteConfirmUserId !== null} onClose={() => setDeleteConfirmUserId(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ color: C.bear }}>Delete User Permanently?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                This will permanently delete user ID <strong>{deleteConfirmUserId}</strong> and all their trades, alerts, and settings. <strong>This cannot be undone.</strong>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmUserId(null)}>Cancel</Button>
              <Button variant="contained" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDeleteUser}>
                Delete Permanently
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>
      )}
      {/* Claim Admin Access Dialog */}
      <Dialog open={promoteAdminOpen} onClose={() => { setPromoteAdminOpen(false); setPromoteDbPassword(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: C.accent }}>Claim Admin Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the database password (DB_PASSWORD from the server .env file) to grant yourself admin access.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Database Password"
            value={promoteDbPassword}
            onChange={(e) => setPromoteDbPassword(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && promoteDbPassword) handlePromoteAdmin(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPromoteAdminOpen(false); setPromoteDbPassword(''); }}>Cancel</Button>
          <Button variant="contained" color="secondary" disabled={!promoteDbPassword || promoteLoading} onClick={handlePromoteAdmin}>
            {promoteLoading ? <CircularProgress size={20} color="inherit" /> : 'Claim Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserSettings;
